import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Icon from '~/components/common/icon';

/**
 * Render tabs in the Entity Header
 */
class Tabs extends PureComponent {
	/**
	 * Constructor
	 *
	 * @param {Object} props
	 */
	constructor( props ) {
		super( props );

		// If there is a currentTab defined, use the currentTab ID, if not, use the first tab.
		const activeTab =
			'' === this.props.data.currentTab
				? 0 < this.props.data.tabs.length
					? this.props.data.tabs[ 0 ].id
					: ''
				: this.props.data.currentTab;

		/**
		 * Tabs data
		 */
		this.state = {
			currentTab: activeTab,
			tabs: this.props.data.tabs || [],
			popover: false,
		};

		this.dropdownRef = React.createRef();
		this.handleTabClick = this.handleTabClick.bind( this );
		this.handleActions = this.handleActions.bind( this );
		this.handleClickOutside = this.handleClickOutside.bind( this );
		this.showMetaboxesDocumentSidebar = this.showMetaboxesDocumentSidebar.bind(
			this
		);
	}

	/**
	 * Hide all tabs except the first
	 * Add mousedown event
	 */
	componentDidMount() {
		const { tabs, currentTab } = this.state;

		// The page has tabs, add a class to body to push down Gutenberg if needed
		if ( 0 !== tabs.length ) {
			document.body.classList.add( 'ld-header-has-tabs' );
		}

		// Look for changes in #editor in Gutenberg
		const editorContainer = document.getElementById( 'editor' );
		if ( editorContainer ) {
			const observer = new MutationObserver( () => {
				const editor = document.querySelector(
					'.edit-post-visual-editor'
				);
				if ( editor ) {
					editor.id = 'post-body-content';
					editor.classList.add( 'tab-content' );
					observer.disconnect();
				}
			} );
			const observerConfig = {
				attributes: false,
				childList: true,
				characterData: false,
			};
			observer.observe( editorContainer, observerConfig );
		}

		// Add necessary class to Gutenberg editor
		window.addEventListener( 'load', () => {
			tabs.map( ( tab ) => {
				const tabEl = document.querySelector( '#' + tab.id );
				if ( tabEl ) {
					tabEl.classList.add( 'tab-content' );
					// Hide all tabs except the current tab
					if ( tab.id !== currentTab ) {
						tabEl.setAttribute( 'aria-hidden', true );
					}
				}
			} );

			document.body.classList.add( 'ld-tabs-ready' );

			// Initialize Metaboxes
			this.showMetaboxesDocumentSidebar( currentTab );
		} );

		// Set active tab data
		if ( '' !== currentTab ) {
			document.body.setAttribute( 'data-active-tab', currentTab );
		}

		document.addEventListener( 'mousedown', this.handleClickOutside );
	}

	/**
	 * Remove mousedown event
	 */
	componentWillUnmount() {
		document.removeEventListener( 'mousedown', this.handleClickOutside );
	}

	/**
	 * Handle Actions click
	 */
	handleActions() {
		this.setState( ( { popover } ) => ( { popover: ! popover } ) );
	}

	/**
	 * Handle clicking on DOM
	 *
	 * @param {handleClickOutside} e
	 */
	handleClickOutside( e ) {
		if (
			this.dropdownRef.current &&
			! this.dropdownRef.current.contains( e.target )
		) {
			this.setState( () => ( { popover: false } ) );
		}
	}

	/**
	 * Switch tab on button click
	 *
	 * @param {handleTabClick} e
	 */
	handleTabClick( e ) {
		e.preventDefault();
		const { tabs } = this.state;
		const { target } = e;
		const controlledTab = target.getAttribute( 'aria-controls' );

		if ( controlledTab ) {
			document.body.setAttribute( 'data-active-tab', controlledTab );

			if ( document.querySelector( 'button[data-label="Document"]' ) ) {
				document
					.querySelector( 'button[data-label="Document"]' )
					.click();
			}

			this.showMetaboxesDocumentSidebar( controlledTab );
		} else {
			document.body.removeAttribute( 'data-active-tab' );
		}
		this.setState( { currentTab: controlledTab } );

		tabs.map( ( tab ) => {
			const tabEl = document.querySelector( '#' + tab.id );
			if ( tabEl ) {
				if ( tab.id !== controlledTab ) {
					tabEl.setAttribute( 'aria-hidden', true );
				} else {
					tabEl.setAttribute( 'aria-hidden', false );
				}
			}
		} );

		// Get last bit of URL including query strings.
		const lastPartOfUrl = window.location.href.substr(
			window.location.href.lastIndexOf( '/' ) + 1
		);

		// Get currentTab if it isn't the first tab (index 0)
		let selectedTab =
			this.state.tabs &&
			this.state.tabs.filter(
				( tab, index ) => controlledTab === tab.id && 0 !== index
			);
		selectedTab = selectedTab[ 0 ] ? selectedTab[ 0 ].id : null;

		// Set the URL with currentTab. Refreshing will preserve the current tab.
		// replaceState() modifies the current history entry instead of creating a new one.
		window.history.replaceState(
			{},
			'',
			this.updateQueryStringParameter(
				lastPartOfUrl,
				'currentTab',
				selectedTab
			)
		);

		// Set the selectedTab value to ld_post_edit_current_tab input field
		const ld_post_edit_current_tab = document.getElementById(
			'ld_post_edit_current_tab'
		);
		if ( ld_post_edit_current_tab ) {
			ld_post_edit_current_tab.value = selectedTab;
		}

		try {
			window.scrollTo( 0, 0 );
		} catch ( e ) {
			// unsupported browser
		}
	}

	/**
	 * Update or Add a Query String Param
	 *
	 * @param {string} uri
	 * @param {string} key
	 * @param {string} value
	 */
	updateQueryStringParameter( uri, key, value ) {
		const re = new RegExp( '([?&])' + key + '=.*?(&|$)', 'i' );
		const separator = -1 !== uri.indexOf( '?' ) ? '&' : '?';
		if ( uri.match( re ) ) {
			if ( value ) {
				return uri.replace( re, '$1' + key + '=' + value + '$2' );
			}
			return uri.replace( re, '' );
		}

		return uri + separator + key + '=' + value;
	}

	/**
	 * Hide all existing Metaboxes and only show the ones that are needed on a particular tab
	 * Show the Document Sidebar only when needed
	 *
	 * @param {number} controlledTab
	 */
	showMetaboxesDocumentSidebar( controlledTab ) {
		// Only run code on post edit screens - because these pages don't reload when changing tabs
		if (
			document.location.href.includes( 'post.php' ) ||
			document.location.href.includes( 'post-new.php' )
		) {
			// Get all known metaboxes via the LearnDashData var onto an array
			let allKnownMetaboxes = [];
			LearnDashData.tabs &&
				LearnDashData.tabs.forEach( ( tab ) => {
					if ( tab.metaboxes ) {
						allKnownMetaboxes = [
							...allKnownMetaboxes,
							...tab.metaboxes,
						];
					}
				} );

			// Get Metabox containers in WordPress
			const metaboxesParents = document.querySelectorAll(
				'#normal-sortables,#advanced-sortables'
			);

			// Loop through the two Metabox containers
			for ( const parent of metaboxesParents ) {
				// Hide all existing Metaboxes in the screen
				let currentMetaboxes = parent.querySelectorAll(
					'.postbox:not(.tab-content)'
				);

				// Converting NodeList to Array to make it owkr on IE
				currentMetaboxes =
					currentMetaboxes && Array.from( currentMetaboxes );

				currentMetaboxes &&
					currentMetaboxes.forEach( ( metabox ) => {
						metabox.style.display = 'none';
					} );

				let firstTab =
					this.state.tabs &&
					this.state.tabs.filter( ( tab, index ) => 0 === index );
				firstTab = firstTab[ 0 ] ? firstTab[ 0 ].id : null;

				// If selected tab is the 'Courses page' tab
				if ( firstTab === controlledTab ) {
					currentMetaboxes &&
						currentMetaboxes.forEach( ( metabox ) => {
							// Show metaboxes that are not known
							if ( ! allKnownMetaboxes.includes( metabox.id ) )
								metabox.style.display = 'block';
						} );
				}
			}
		}

		// Show only relevant Metaboxes and hide Document Sidebar when not needed
		this.state.tabs.forEach( ( tab ) => {
			if ( controlledTab === tab.id ) {
				tab.metaboxes &&
					tab.metaboxes.forEach( ( metabox ) => {
						const selctedMetabox = document.getElementById(
							metabox
						);
						if ( selctedMetabox ) {
							selctedMetabox.style.display = 'block';
						}
					} );

				// Get reference to .edit-post-sidebar and .edit-post-layout__content elements
				const documentSidebar = document.querySelector(
					'.edit-post-sidebar'
				);
				const documentLayoutContent = document.querySelector(
					'.edit-post-layout__content'
				);
				const gearIcon = document.querySelector(
					'.components-button.components-icon-button[aria-label="Settings"]'
				);
				const noticeList = document.querySelector(
					'.edit-post-layout.is-sidebar-opened .components-notice-list'
				);

				// If elments exist and if sidebar is to be hidden
				if (
					documentSidebar &&
					documentLayoutContent &&
					tab.showDocumentSidebar &&
					'false' === tab.showDocumentSidebar
				) {
					documentSidebar.style.display = 'none';
					if ( window.isRtl ) {
						documentLayoutContent.style.marginLeft = 0;
					} else {
						documentLayoutContent.style.marginRight = 0;
					}
					if ( gearIcon ) {
						gearIcon.style.display = 'none';
					}
					if ( noticeList ) {
						noticeList.style.right = 0;
					}
				} else if ( documentSidebar && documentLayoutContent ) {
					documentSidebar.style.display = 'block';
					if ( window.isRtl ) {
						documentLayoutContent.style.marginLeft = null;
					} else {
						documentLayoutContent.style.marginRight = null;
					}
					if ( gearIcon ) {
						gearIcon.style.display = null;
					}
					if ( noticeList ) {
						noticeList.style.right = null;
					}
				}
			}
		} );
	}

	/**
	 * Render the tabs component
	 *
	 * @event handleClickOutside
	 * @event handleTabClick
	 */
	render() {
		const { tabs, currentTab, popover } = this.state;
		const addNewEntityButton = document.querySelector(
			'.page-title-action'
		);
		const currentTabObject =
			tabs.filter( ( tab ) => currentTab === tab.id )[ 0 ] || tabs[ 0 ];
		const hasActions =
			currentTabObject &&
			'actions' in currentTabObject &&
			0 !== currentTabObject.actions.length;

		if ( 0 !== tabs.length ) {
			return (
				<Fragment>
					{ addNewEntityButton && (
						<a
							href={ addNewEntityButton.href }
							className={ `global-new-entity-button ${
								hasActions ? '' : '-right'
							}` }
						>
							{ addNewEntityButton.innerText }
						</a>
					) }
					<div className="ld-tab-buttons">
						{ tabs.map( ( tab, index ) =>
							'true' === tab.isExternal ? (
								<a
									key={ `tab-button-${ index }` }
									id={ tab.id ? `tab-${ tab.id }` : false }
									href={ tab.link.replace( /&amp;/g, '&' ) }
									className={ `is-button button components-button ${
										tab.id === currentTab
											? 'is-primary'
											: ''
									}` }
									dangerouslySetInnerHTML={ {
										__html: tab.name,
									} }
								/>
							) : (
								<button
									key={ `tab-button-${ index }` }
									id={ tab.id ? `tab-${ tab.id }` : false }
									onClick={ this.handleTabClick }
									aria-controls={ tab.id }
									data-index={ index }
									className={ `is-button button components-button ${
										tab.id === currentTab
											? 'is-primary'
											: ''
									}` }
									dangerouslySetInnerHTML={ {
										__html: tab.name,
									} }
								/>
							)
						) }
						{ hasActions && (
							<div
								className="edit-post-header__settings"
								ref={ this.dropdownRef }
							>
								<button
									className="is-button components-button ld-actions button"
									onClick={ this.handleActions }
									aria-haspopup="true"
									aria-expanded={ popover }
								>
									{ LearnDashData.i18n.actions }
									<Icon icon="dropdown" />
								</button>
								{ popover && currentTabObject.actions && (
									<div className="components-popover edit-post-more-menu__content is-bottom is-left ld-popover">
										<div className="components-popover__content">
											{ currentTabObject.actions.map(
												( action, index ) => {
													// Checking if current URL is equal to link
													// Also checking if the URL (converted to &amp) contains the link
													const isActive =
														document.URL ===
															action.link ||
														0 <
															document.URL.replace(
																'&',
																'&amp;'
															).indexOf(
																action.link
															);
													const activeClassName = isActive
														? ' active'
														: '';
													return 'true' ===
														action.isCallback ? (
														<button
															key={ `action-${ index }` }
															id={
																action.id
																	? `action-${ action.id }`
																	: false
															}
															className={ `${
																action.classNames &&
																action
																	.classNames
																	.length
																	? action.classNames.join(
																			' '
																	  )
																	: ''
															} components-menu-item__button components-button${ activeClassName }` }
															onClick={ Function(
																action.callback
															) }
														>
															{ action.title }
														</button>
													) : (
														<a
															key={ `action-${ index }` }
															id={
																action.id
																	? `action-${ action.id }`
																	: false
															}
															href={ action.link.replace(
																/&amp;/g,
																'&'
															) }
															target={
																'true' ===
																action.isExternal
																	? '_blank'
																	: ''
															}
															className={ `${
																action.classNames &&
																action
																	.classNames
																	.length
																	? action.classNames.join(
																			' '
																	  )
																	: ''
															} components-menu-item__button components-button${ activeClassName }` }
														>
															{ action.title }
														</a>
													);
												}
											) }
										</div>
									</div>
								) }
							</div>
						) }
					</div>
				</Fragment>
			);
		}
		return null;
	}
}

/**
 * Valid props
 */
Tabs.propTypes = {
	data: PropTypes.object,
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = ( state ) => ( {
	...state,
} );

/**
 *
 */
const mapDispatchToProps = () => ( {} );

export default connect( mapStateToProps, mapDispatchToProps )( Tabs );
